'use client';

import dynamic from 'next/dynamic';
import { type ComponentProps, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import logSignUpScreenContinueButtonTapped from '@alltrails/amplitude/events/logSignUpScreenContinueButtonTapped';
import useSetUser from '@alltrails/context/hooks/useSetUser';
import { setCurrencyCode } from '@alltrails/context/slices/currencyCodeSlice';
import { setPlans } from '@alltrails/context/slices/plansSlice';
import Typography from '@alltrails/core/components/Typography';
import logError from '@alltrails/core/utils/logError';
import Button from '@alltrails/denali/components/Button';
import Heading from '@alltrails/denali/components/Heading';
import type { TrailId } from '@alltrails/types/TrailId';
import AltLoginsForm from '@alltrails/user-auth/components/AltLoginsForm';
import GoogleLoginButton from '@alltrails/user-auth/components/GoogleLoginButton';
import ContinueInAppButton from '@alltrails/user-auth/components/SignUpModal/ContinueInAppButton';
import useApplePopupHandler from '@alltrails/user-auth/hooks/useApplePopupHandler';
import useFacebookHandler from '@alltrails/user-auth/hooks/useFacebookHandler';
import useGoogleHandler from '@alltrails/user-auth/hooks/useGoogleHandler';
import type LoginResponse from '@alltrails/user-auth/types/LoginResponse';
import styles from './ReviewsAuthWall.module.scss';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

type Props = {
  trailId: TrailId;
};

type AltLoginHandlers = ComponentProps<typeof AltLoginsForm>;

// Static auth wall that gates the reviews section for the reviews-wall treatments.
// Collapsed by default (Continue in app / Google / More options); "More options" reveals the
// remaining social logins + email inline. Social buttons log the user in place (the wall
// disappears once useUser() is truthy); "Continue with email" opens the shared SignUpModal
// directly on the email form.

// Figma: https://www.figma.com/design/of9BVPuMqzjbOWZMEDcGxX/New-User-Journey-2026?node-id=2610-129001&m=dev
export default function ReviewsAuthWall({ trailId }: Props) {
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const setUser = useSetUser();
  const dispatch = useDispatch();
  const applePopupHandler = useApplePopupHandler(true);
  const facebookHandler = useFacebookHandler();
  const googleHandler = useGoogleHandler();

  // Mirrors SignUpModal's non-redirect success path so a successful inline login
  // updates context and ungates the reviews without a full navigation.
  const onAuthSuccess = (response: LoginResponse) => {
    setUser(response.user);
    dispatch(setPlans(response.plans));
    dispatch(setCurrencyCode(response.currencyCode));
  };

  const appleLoginHandler: AltLoginHandlers['appleLoginHandler'] = async () => {
    logSignUpScreenContinueButtonTapped({ signup_screen_continue_button_type: 'apple' });
    try {
      const loginResponse = await applePopupHandler({ handlerType: 'signup' });
      if (loginResponse) {
        onAuthSuccess(loginResponse);
      }
    } catch (error) {
      logError(new Error('ReviewsAuthWall Apple login failed', { cause: error }));
    }
  };

  const googleLoginHandler: AltLoginHandlers['googleLoginHandler'] = async response => {
    try {
      const loginResponse = await googleHandler({ googleUserOrToken: response, handlerType: 'signup' });
      onAuthSuccess(loginResponse);
    } catch (error) {
      logError(new Error('ReviewsAuthWall Google login failed', { cause: error }));
    }
  };

  const facebookLoginHandler: AltLoginHandlers['facebookLoginHandler'] = async response => {
    try {
      const loginResponse = await facebookHandler({ loginInfo: response, handlerType: 'signup' });
      onAuthSuccess(loginResponse);
    } catch (error) {
      logError(new Error('ReviewsAuthWall Facebook login failed', { cause: error }));
    }
  };

  return (
    <div className={styles.wall}>
      <div className={styles.fade} />
      <div className={styles.card}>
        <Heading as="h3" className={styles.heading} size="500">
          <FormattedMessage defaultMessage="See what others are saying about this trail" />
        </Heading>
        <Typography className={styles.subtext} color="secondary" component="p" variant="text200">
          <FormattedMessage defaultMessage="Create an account to read reviews and get trail insights from the community." />
        </Typography>
        <ContinueInAppButton deeplinkData={{ pageId: trailId, pageType: 'trail' }} displayTrigger="scroll auth" flatColors />
        <div className={styles.divider}>
          <span className={styles.dividerText}>
            <FormattedMessage defaultMessage="or" />
          </span>
        </div>
        {showMoreOptions ? (
          <>
            <AltLoginsForm
              appleLoginHandler={appleLoginHandler}
              authType="signup"
              facebookLoginHandler={facebookLoginHandler}
              flatColors
              googleLoginHandler={googleLoginHandler}
            />
            <Button
              onClick={() => {
                logSignUpScreenContinueButtonTapped({ signup_screen_continue_button_type: 'email' });
                setIsEmailModalOpen(true);
              }}
              text={<FormattedMessage defaultMessage="Continue with email" />}
              variant="secondary"
              width="full"
            />
          </>
        ) : (
          <>
            <GoogleLoginButton flatColors googleLoginHandler={googleLoginHandler} />
            <Button
              onClick={() => setShowMoreOptions(true)}
              text={<FormattedMessage defaultMessage="More options" />}
              variant="secondary"
              width="full"
            />
          </>
        )}
      </div>
      {isEmailModalOpen && (
        <SignUpModal
          closeModal={() => setIsEmailModalOpen(false)}
          deeplinkData={{ pageId: trailId, pageType: 'trail' }}
          initialStep="email-check"
          isOpen={isEmailModalOpen}
          onSuccess={() => setIsEmailModalOpen(false)}
          trigger="scroll auth"
          upsellTrigger="scroll auth"
        />
      )}
    </div>
  );
}
